import React, { createContext, useState, useContext, useEffect } from 'react';
import { authAPI } from '../services/api';
import toast from 'react-hot-toast';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    checkAuth();
  }, []);

  // Sync saved theme from user profile to ThemeContext via custom event
  const syncTheme = (user) => {
    const tid = user?.companySettings?.themeId;
    const tc  = user?.companySettings?.themeCustom;
    if (tid) {
      window.dispatchEvent(new CustomEvent('pm-theme-sync', { detail: { themeId: tid, themeCustom: tc } }));
    }
  };

  const checkAuth = async () => {
    const token = localStorage.getItem('token');
    if (token) {
      try {
        const response = await authAPI.getMe();
        setUser(response.data.user);
        setIsAuthenticated(true);
        syncTheme(response.data.user);
      } catch (error) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        setIsAuthenticated(false);
      }
    }
    setLoading(false);
  };

  const login = async (credentials) => {
    try {
      const response = await authAPI.login(credentials);
      const { token, user } = response.data;

      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(user));

      setUser(user);
      setIsAuthenticated(true);
      syncTheme(user);

      toast.success(`Welcome back, ${user.name}!`);
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.message || 'Login failed';
      toast.error(message);
      return { success: false, error: message };
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
    setIsAuthenticated(false);
    toast.success('Logged out successfully');
  };

  const updateUser = (updatedUser) => {
    setUser(updatedUser);
    localStorage.setItem('user', JSON.stringify(updatedUser));
  };

  const TEAM_DEFAULTS = [
    'dashboard','projects','tasks','attendance','leaves',
    'time_tracking','daily_reports','wfh','calendar','shifts',
    'onboarding','training','messages','notifications','settings'
  ];

  const MANAGER_DEFAULTS = [
    'dashboard','projects','tasks','daily_reports','messages','notifications','settings',
    'team','hr','attendance','leaves','salary','holidays','employee_accounts',
    'performance','wfh','calendar','onboarding','training','shifts',
    'expenses','invoices','payment_accounts','profit_analytics','revenue_forecast',
    'clients','crm','upwork',
    'analytics','workload','daily_work','time_approval','templates','task_templates','audit_log',
    'permissions'
  ];

  // Check if current user has access to a module key
  const hasPermission = (module) => {
    if (!user) return false;
    if (user.role === 'admin') return true; // admin always has everything
    if (user.role === 'client') return ['dashboard','projects','tasks','messages','notifications','settings'].includes(module);
    if (user.role === 'manager') {
      // manager: use custom permissions if set, otherwise manager defaults
      const perms = user.permissions || MANAGER_DEFAULTS;
      return perms.includes(module);
    }
    // team: use custom permissions if set, otherwise team defaults
    const perms = user.permissions || TEAM_DEFAULTS;
    return perms.includes(module);
  };

  const value = {
    user,
    loading,
    isAuthenticated,
    login,
    logout,
    updateUser,
    checkAuth,
    hasPermission,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
